/*
 * a11y-slick.js — repairs the non-conformant ARIA that slick-slider (bundled in
 * vendor.js) writes onto every carousel. Slick's accessibility layer predates the
 * current ARIA spec and emits markup that fails several axe/Lighthouse audits:
 *
 *   .slick-dots      role="tablist"  with no role="tab" children
 *   .slick-dots li   role="presentation" + aria-selected  (role forbids the attr)
 *   .slick-track     role="listbox"  with no accessible name
 *   .slick-slide     role="option"/"presentation", aria-selected,
 *                    aria-controls pointing at IDs that never exist
 *   aria-hidden="true" slides that still contain tabbable buttons/links
 *
 * Rather than invent a tab/listbox pattern the markup does not actually implement,
 * we strip the bogus roles and let the native semantics (ul/li, button, a) stand,
 * then keep offscreen slides out of the tab order — which is the real bug for
 * keyboard users, and the one thing slick gets wrong that genuinely traps focus.
 *
 * Loaded deferred; safe to run repeatedly and before/after any slick init.
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href],button,input,select,textarea,iframe,[tabindex]';

  function cleanSlider(root) {
    if (!root || !root.querySelectorAll) return;

    root.querySelectorAll('.slick-dots').forEach(function (dots) {
      dots.removeAttribute('role');
      dots.querySelectorAll('li').forEach(function (li) {
        li.removeAttribute('role');
        li.removeAttribute('aria-selected');
        li.removeAttribute('aria-controls');
        // slick marks every inactive dot aria-hidden="true" while leaving its
        // button tabbable -> "aria-hidden element contains focusable descendant".
        // Dots are navigation controls: all of them should be announced, so drop
        // the attribute rather than pulling the buttons out of the tab order.
        li.removeAttribute('aria-hidden');
      });
    });

    root.querySelectorAll('.slick-track').forEach(function (track) {
      if (track.getAttribute('role') === 'listbox') track.removeAttribute('role');
    });

    root.querySelectorAll('.slick-slide').forEach(function (slide) {
      var r = slide.getAttribute('role');
      if (r === 'option' || r === 'presentation' || r === 'tabpanel') slide.removeAttribute('role');
      slide.removeAttribute('aria-selected');
      slide.removeAttribute('aria-controls');
      slide.removeAttribute('aria-describedby');

      // Offscreen slides must not be reachable by Tab.
      var hidden = slide.getAttribute('aria-hidden') === 'true';
      slide.querySelectorAll(FOCUSABLE).forEach(function (el) {
        if (hidden) {
          if (el.getAttribute('tabindex') !== '-1') {
            el.setAttribute('tabindex', '-1');
            el.setAttribute('data-a11y-slick-tab', '');
          }
        } else if (el.hasAttribute('data-a11y-slick-tab')) {
          el.removeAttribute('tabindex');
          el.removeAttribute('data-a11y-slick-tab');
        }
      });
    });
  }

  function cleanAll() {
    document.querySelectorAll('.slick-slider,.slick-initialized').forEach(cleanSlider);
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    // setTimeout, not rAF: rAF is throttled in background tabs and never fired
    // for sliders that initialise late (lookbook), leaving them uncleaned.
    window.setTimeout(function () {
      queued = false;
      cleanAll();
    }, 0);
  }

  // 1) slick's own lifecycle events (delegated, so sliders built later are covered)
  if (window.jQuery) {
    window.jQuery(document).on('init reInit afterChange setPosition', '.slick-slider', function (e) {
      cleanSlider(e.currentTarget);
    });
  }

  // 2) sliders that initialised before this file ran, plus AJAX-injected ones
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  window.addEventListener('load', schedule);

  // data-loader-script builds some sliders only once they scroll into view, which
  // can be long after load. Cheap catch-up sweeps + a scroll hook cover those.
  [400, 1200, 3000].forEach(function (t) { window.setTimeout(schedule, t); });
  window.addEventListener('scroll', schedule, { passive: true });

  // 3) slick rewrites aria-hidden on every slide change; catch those mutations
  if ('MutationObserver' in window) {
    new MutationObserver(schedule).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributeFilter: ['aria-hidden', 'class', 'role', 'aria-selected']
    });
  }
})();
